import {
  CellClassParams,
  ColDef,
  GridApi,
  GridOptions,
  ICellRendererParams,
  IsGroupOpenByDefaultParams,
  Module,
  ValueFormatterParams,
  ValueGetterParams,
} from '@ag-grid-community/core'
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
} from '@angular/core'
import {
  enableHeliosCopyBehavior,
  heliosClientSideRowConfig,
} from '@helios/ag-grid-config'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import {
  BehaviorSubject,
  combineLatest,
  concat,
  Observable,
  of,
  Subject,
} from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  map,
  shareReplay,
  skip,
  switchMap,
  takeUntil,
} from 'rxjs/operators'
import { gridKeyOf, RPS_SOURCE } from '../../models/source-key.model'
import {
  ComparisonSource,
  PositionComparison,
} from '../../models/position-comparison.model'
import { FlexPositionComparisonService } from '../../services/flex-position-comparison.service'
import { WorkbenchStoreService } from '../../services/workbench-store.service'
import { SubPanelEmptyState, subPanelEmptyState } from '../sub-panel-tab.model'
import {
  bucketFields,
  differenceKeys,
  diffDisplayFor,
  FieldRow,
  matchesSearch,
  resolveCellValue,
} from './flex-position-comparison.util'

/** What the template renders: the loaded comparison, an empty-state card, a load error, or the
 *  in-flight spinner. */
export interface ComparisonViewModel {
  /** Present when a single anchor is selected and its comparison has loaded. */
  comparison: PositionComparison | null
  /** Present when 0 or >1 rows are selected (the shared card wording). */
  emptyState: SubPanelEmptyState | null
  /** Present when the comparison request failed. */
  error: boolean
  /** The request is in flight — mutually exclusive with `comparison`/`emptyState`/`error`. */
  loading: boolean
}

const emptyStateVm = (): ComparisonViewModel => ({
  comparison: null,
  emptyState: subPanelEmptyState(),
  error: false,
  loading: false,
})

const loadingVm = (): ComparisonViewModel => ({
  comparison: null,
  emptyState: null,
  error: false,
  loading: true,
})

/** A field row plus its resolved Difference-column display against the currently-selected
 *  Difference key (see {@link diffDisplayFor}). `diffTone` drives both the display color AND the
 *  Differences Only / Comparisons Only filters, so what's filtered always matches what's shown —
 *  the server's raw verdict can be 'MATCH'/'BREAK' or (for some numeric fields) the delta itself as
 *  a number, so tone is the only value both filtering and rendering can rely on consistently. */
interface DisplayRow extends FieldRow {
  diffText: string
  diffTone: 'match' | 'break' | 'missing'
}

/** The COMPARED table section, or one `<SOURCE> ONLY` section — rendered as an ag-Grid row group. */
export interface Section {
  /** `'compared'`, or `only:<source>` — stable across reloads. */
  key: string
  title: string
  rows: DisplayRow[]
}

const COMPARED_KEY = 'compared'
const onlySectionKey = (source: string): string => `only:${source}`

/** A DisplayRow flattened for the grid, tagged with which section it belongs to — the grid groups
 *  rows by `groupKey` (see {@link isGroupOpenByDefault}) to get the COMPARED / `<SOURCE> ONLY`
 *  collapsible headers ag-Grid renders natively. `groupKey` is the stable {@link Section.key}, not
 *  the rendered title: the title carries a row count that changes on every filter/search edit, and
 *  grouping on it would reset each section's expand state and re-derive a fresh group every time. */
export interface GridRow extends DisplayRow {
  groupKey: string
  groupTitle: string
}

export const flattenRows = (sections: Section[]): GridRow[] =>
  sections.flatMap(section =>
    section.rows.map(row => ({
      ...row,
      groupKey: section.key,
      groupTitle: section.title,
    })),
  )

/** `<SOURCE> ONLY` sections start collapsed, COMPARED starts expanded — same default as the
 *  hand-rolled table this replaced, which kept this exact predicate on the section key. */
export const isGroupOpenByDefault = (
  params: IsGroupOpenByDefaultParams,
): boolean => !String(params.rowNode.key ?? '').startsWith('only:')

/** One ColDef per visible source, plus the Field and Difference columns. Rebuilt whenever the
 *  visible sources or the selected Difference key change, mirroring the reload-time
 *  `gridApi.setColumnDefs()` pattern used by the 1.0 External Comparison panel. */
const rowOf = (params: { data?: unknown }): GridRow | undefined =>
  params.data as GridRow | undefined

/** The human-readable label for a section's group row, from the stable key ag-Grid groups by.
 *  `groupDisplayType: 'groupRows'` draws the group row from `gridOptions.groupRowRendererParams`,
 *  not from the row-group column's own `valueFormatter`/`cellRendererParams` — those are for the
 *  default single-column group display, and are silently ignored in 'groupRows' mode. */
export const sectionTitleFor = (sections: Section[], key: string): string =>
  sections.find(section => section.key === key)?.title ?? key

export const buildColumnDefs = (vm: DisplayViewModel): ColDef[] => {
  const sourceColumns: ColDef[] = vm.visibleSources.map(source => ({
    colId: source.source,
    headerName: source.source,
    flex: 1,
    valueGetter: (params: ValueGetterParams) => {
      const row = rowOf(params)
      return row ? resolveCellValue(row, source.source) : null
    },
    valueFormatter: (params: ValueFormatterParams) =>
      params.value == null ? '—' : String(params.value),
  }))

  const differenceColumn: ColDef[] = vm.isMultiSource
    ? [
        {
          colId: 'difference',
          headerName:
            vm.comparison && vm.differenceKeys.length === 1
              ? `${vm.comparison.anchor} vs ${vm.differenceKeys[0]}`
              : 'Difference',
          flex: 1,
          valueGetter: (params: ValueGetterParams) =>
            rowOf(params)?.diffText ?? '—',
          cellClassRules: {
            'comparison-cell--match': (params: CellClassParams) =>
              rowOf(params)?.diffTone === 'match',
            'comparison-cell--break': (params: CellClassParams) =>
              rowOf(params)?.diffTone === 'break',
            'comparison-cell--missing': (params: CellClassParams) =>
              rowOf(params)?.diffTone === 'missing',
          },
        },
      ]
    : []

  return [
    // ag-Grid groups off a colDef's `field` (or a valueGetter) — `field: 'groupKey'` is what tells
    // it which property to group rows by; `colId` alone (no field) groups nothing. Grouping on the
    // stable key rather than the rendered (count-bearing) title keeps a section's identity — and
    // its expand/collapse state — unaffected by a row count that changes on every filter edit. The
    // human-readable label is drawn separately, by `gridOptions.groupRowRendererParams` — see
    // {@link sectionTitleFor}.
    {
      colId: 'groupKey',
      field: 'groupKey',
      rowGroup: true,
      hide: true,
    },
    {
      colId: 'label',
      headerName: 'Field',
      flex: 1,
      valueGetter: (params: ValueGetterParams) => rowOf(params)?.label ?? '',
    },
    ...sourceColumns,
    ...differenceColumn,
  ]
}

/** Everything the template needs to render the table + toolbar, after bucketing and filtering. */
export interface DisplayViewModel {
  emptyState: SubPanelEmptyState | null
  error: boolean
  loading: boolean
  comparison: PositionComparison | null
  visibleSources: ComparisonSource[]
  differenceKeys: string[]
  differenceKey: string | null
  sections: Section[]
  fieldsCount: number
  differencesOnly: boolean
  comparisonsOnly: boolean
  search: string
  /** Whether more than one source is present (false for an anchor-only/unmatched position) — gates
   *  the Differences Only / Comparisons Only toggles and the Difference column, none of which mean
   *  anything with nothing to compare against. Mirrors the design mock's `multi` flag. */
  isMultiSource: boolean
}

/** Toolbar state. */
export interface FilterState {
  hiddenSources: Set<string>
  differencesOnly: boolean
  comparisonsOnly: boolean
  search: string
  differenceKey: string | null
}

export const defaultFilters = (): FilterState => ({
  hiddenSources: new Set<string>(),
  differencesOnly: false,
  comparisonsOnly: false,
  search: '',
  differenceKey: null,
})

const emptyDisplayVm = (
  vm: ComparisonViewModel,
  filters: FilterState,
): DisplayViewModel => ({
  emptyState: vm.emptyState,
  error: vm.error,
  loading: vm.loading,
  comparison: null,
  visibleSources: [],
  differenceKeys: [],
  differenceKey: null,
  sections: [],
  fieldsCount: 0,
  differencesOnly: filters.differencesOnly,
  comparisonsOnly: filters.comparisonsOnly,
  search: filters.search,
  isMultiSource: false,
})

/** Buckets + filters a loaded comparison into what the toolbar/table actually render. */
export function buildDisplayVm(
  vm: ComparisonViewModel,
  filters: FilterState,
): DisplayViewModel {
  if (vm.comparison == null) return emptyDisplayVm(vm, filters)

  const comparison = vm.comparison
  const keys = differenceKeys(comparison)
  const isMultiSource = comparison.sources.length > 1
  const differenceKey = filters.differenceKey ?? keys[0] ?? null
  const visibleSources = comparison.sources.filter(
    source => !filters.hiddenSources.has(source.source),
  )
  const { compared, onlyBySource } = bucketFields(comparison)

  const withDiff = (row: FieldRow): DisplayRow => {
    const diff = differenceKey
      ? diffDisplayFor(comparison, differenceKey, row.label)
      : { text: '—', tone: 'missing' as const }
    return { ...row, diffText: diff.text, diffTone: diff.tone }
  }

  // Differences Only keeps break/missing (hides match); Comparisons Only keeps rows that actually
  // produced a comparison (hides missing) — applied the same way to every section, per the design
  // mock (a field's bucket doesn't change what these two filters mean for it). Filtering on
  // diffTone rather than the server's raw verdict keeps this consistent with what's on screen.
  const passesFilters = (row: DisplayRow): boolean =>
    matchesSearch(row, filters.search) &&
    !(filters.differencesOnly && row.diffTone === 'match') &&
    !(filters.comparisonsOnly && row.diffTone === 'missing')

  const comparedRows = compared.map(withDiff).filter(passesFilters)

  const sections: Section[] = []
  if (comparedRows.length) {
    sections.push({
      key: COMPARED_KEY,
      title: `COMPARED ${comparedRows.length}`,
      rows: comparedRows,
    })
  }
  visibleSources.forEach(source => {
    const rows = (onlyBySource[source.source] ?? [])
      .map(withDiff)
      .filter(passesFilters)
    if (!rows.length) return
    const noun = isMultiSource ? 'ONLY' : 'FIELDS'
    sections.push({
      key: onlySectionKey(source.source),
      title: `${source.source} ${noun} ${rows.length}`,
      rows,
    })
  })

  // Derived from the rendered sections, not the raw buckets: a hidden source's ONLY fields (and
  // anything Search/Differences Only/Comparisons Only filtered out) are not on screen, so the
  // footer must not count them either.
  const fieldsCount = sections.reduce(
    (sum, section) => sum + section.rows.length,
    0,
  )

  return {
    emptyState: null,
    error: false,
    loading: false,
    comparison,
    visibleSources,
    differenceKeys: keys,
    differenceKey,
    sections,
    fieldsCount,
    differencesOnly: filters.differencesOnly,
    comparisonsOnly: filters.comparisonsOnly,
    search: filters.search,
    isMultiSource,
  }
}

/**
 * Position Comparison sub-panel.
 *
 * Driven by {@link WorkbenchStoreService.anchorRow} — whichever row the two grids currently agree
 * is the anchor (the most recently selected one; see the store for the cross-grid tie-break rule).
 *
 * Presentation only: source fields and diff verdicts are rendered verbatim as delivered — nothing
 * is re-diffed client-side. What IS derived client-side, per the design mock, is purely
 * presentational bucketing: which fields are COMPARED (present in >=2 sources) vs `<SOURCE> ONLY`
 * (present in exactly one), which sections are collapsed, and which sources/rows the
 * Sources/Differences Only/Comparisons Only/Search controls currently show. See
 * {@link buildDisplayVm} and flex-position-comparison.util.
 */
@Component({
  selector: 'hs-position-comparison',
  templateUrl: './position-comparison.component.html',
  styleUrls: ['./position-comparison.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PositionComparisonComponent implements OnDestroy {
  readonly vm$: Observable<ComparisonViewModel>
  readonly display$: Observable<DisplayViewModel>
  /** Flattened rows for `nf-table-layout`'s `[data]` input — reactive, unlike columns, which need
   *  an imperative `gridApi.setColumnDefs()` (see the constructor subscription below). */
  readonly rows$: Observable<GridRow[]>

  readonly gridModules: Module[] = [RangeSelectionModule]
  /** @internal */
  gridApi?: GridApi
  readonly gridOptions: GridOptions = {
    ...heliosClientSideRowConfig,
    groupDisplayType: 'groupRows',
    isGroupOpenByDefault,
    // 'groupRows' draws the group row from these params, not from the (hidden) row-group column's
    // own valueFormatter/cellRendererParams — see buildColumnDefs and sectionTitleFor.
    groupRowRendererParams: {
      suppressCount: true,
      innerRenderer: (params: ICellRendererParams) =>
        sectionTitleFor(this.latestVm.sections, params.value as string),
    },
    defaultColDef: {
      sortable: false,
      suppressMenu: true,
      menuTabs: [],
    },
    onGridReady: ({ api }) => {
      this.gridApi = api
      enableHeliosCopyBehavior(api)
      this.gridApi.setColumnDefs(buildColumnDefs(this.latestVm))
    },
  }

  private latestVm: DisplayViewModel = emptyDisplayVm(
    emptyStateVm(),
    defaultFilters(),
  )

  private readonly filters$ = new BehaviorSubject<FilterState>(defaultFilters())
  private readonly destroy$ = new Subject<void>()

  constructor(
    @Inject(FlexPositionComparisonService)
    private readonly comparisonService: FlexPositionComparisonService,
    private readonly store: WorkbenchStoreService,
  ) {
    this.vm$ = this.store.anchorRow().pipe(
      switchMap(anchor => {
        if (!anchor) return of(emptyStateVm())
        const source = RPS_SOURCE[gridKeyOf(anchor.origin)]
        return concat(
          of(loadingVm()),
          this.comparisonService.getComparison(source, anchor.rowId).pipe(
            map(
              (comparison): ComparisonViewModel => ({
                comparison,
                emptyState: null,
                error: false,
                loading: false,
              }),
            ),
            catchError(() =>
              of<ComparisonViewModel>({
                comparison: null,
                emptyState: null,
                error: true,
                loading: false,
              }),
            ),
          ),
        )
      }),
    )

    // A fresh anchor drops any Sources/Difference/Search state left over from the previous
    // position; skip(1) since the initial anchor has nothing to reset from. Keyed on rowId, not
    // anchorRow()'s reference equality: a same-position refetch (a same-account row refresh, or
    // the future load-readiness retry) hands back a new object for the same rowId, and that must
    // refresh the comparison data without throwing away the view state for a position the user
    // never actually changed away from.
    this.store
      .anchorRow()
      .pipe(
        skip(1),
        distinctUntilChanged((a, b) => a?.rowId === b?.rowId),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.filters$.next(defaultFilters()))

    // shareReplay so the column-defs subscription below and the template's own `display$ | async`
    // share one execution of vm$'s switchMap — without it, each subscriber would re-trigger
    // anchorRow()'s switchMap and double up the comparison request.
    this.display$ = combineLatest([this.vm$, this.filters$]).pipe(
      map(([vm, filters]) => buildDisplayVm(vm, filters)),
      shareReplay({ bufferSize: 1, refCount: true }),
    )

    this.rows$ = this.display$.pipe(map(vm => flattenRows(vm.sections)))

    // Columns depend on which sources are visible and which Difference key is selected, neither of
    // which `[data]` covers — ag-Grid needs an explicit setColumnDefs() for a column-shape change,
    // same as the 1.0 External Comparison panel's reloadData().
    this.display$.pipe(takeUntil(this.destroy$)).subscribe(vm => {
      this.latestVm = vm
      this.gridApi?.setColumnDefs(buildColumnDefs(vm))
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  onSearchChange(event: Event): void {
    const search = (event.target as HTMLInputElement).value
    this.filters$.next({ ...this.filters$.value, search })
  }

  onDifferencesOnlyChange(event: Event): void {
    const differencesOnly = (event.target as HTMLInputElement).checked
    this.filters$.next({ ...this.filters$.value, differencesOnly })
  }

  onComparisonsOnlyChange(event: Event): void {
    const comparisonsOnly = (event.target as HTMLInputElement).checked
    this.filters$.next({ ...this.filters$.value, comparisonsOnly })
  }

  onDifferenceKeyChange(key: string): void {
    this.filters$.next({ ...this.filters$.value, differenceKey: key })
  }

  /** Toggles a source's column/section visibility. The anchor can't be hidden. */
  toggleSource(comparison: PositionComparison, source: string): void {
    if (source === comparison.anchor) return
    const hiddenSources = new Set(this.filters$.value.hiddenSources)
    if (hiddenSources.has(source)) hiddenSources.delete(source)
    else hiddenSources.add(source)
    this.filters$.next({ ...this.filters$.value, hiddenSources })
  }

  isSourceHidden(source: string): boolean {
    return this.filters$.value.hiddenSources.has(source)
  }

  /** Stable identity for the source-column *ngFor loop. */
  trackBySource(_index: number, source: { source: string }): string {
    return source.source
  }
}
